import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';
import {
  fetchFeedbackCampaign,
  createFeedbackCampaign,
  updateFeedbackCampaign,
  addFeedbackQuestion,
  updateFeedbackQuestion,
  deleteFeedbackQuestion,
  fetchCountries,
  FeedbackCampaign,
  FeedbackCampaignInput,
  FeedbackQuestion,
  FeedbackQuestionType,
} from '../services/api.ts';
import './FeedbackCampaignEditor.css';

const QUESTION_TYPES: Array<{ value: FeedbackQuestionType; label: string }> = [
  { value: 'rating', label: 'Rating (1–5)' },
  { value: 'nps', label: 'NPS (0–10)' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multi_choice', label: 'Multiple choice' },
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'yes_no', label: 'Yes / No' },
];

const CHOICE_TYPES = new Set(['single_choice', 'multi_choice']);

const toLocalInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const fromLocalInput = (local: string) => new Date(local).toISOString();

const emptyQuestion = (orderIndex: number): FeedbackQuestion => ({
  type: 'rating',
  text: '',
  required: true,
  order_index: orderIndex,
  options: null,
  config: null,
});

const FeedbackCampaignEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Array<{ id: number; name: string }>>([]);

  const [form, setForm] = useState<FeedbackCampaignInput>({
    title: '',
    description: '',
    country_id: null,
    platform: 'both',
    target_sample_size: 500,
    starts_at: '',
    ends_at: '',
    status: 'draft',
    min_app_version: null,
    min_account_age_days: 7,
    questions: [],
  });
  const [savedQuestionIds, setSavedQuestionIds] = useState<Map<number, number>>(new Map());

  const loadCountries = useCallback(async () => {
    try {
      const list = await fetchCountries(0, 200);
      setCountries(list || []);
    } catch {
      // silent — users can still leave country blank
    }
  }, []);

  const loadCampaign = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const c: FeedbackCampaign = await fetchFeedbackCampaign(Number(id));
      setForm({
        title: c.title,
        description: c.description || '',
        country_id: c.country_id,
        platform: c.platform,
        target_sample_size: c.target_sample_size,
        starts_at: toLocalInput(c.starts_at),
        ends_at: toLocalInput(c.ends_at),
        status: c.status,
        min_app_version: c.min_app_version,
        min_account_age_days: c.min_account_age_days,
        questions: (c.questions || []).map((q) => ({
          ...q,
          options: q.options || null,
          config: q.config || null,
        })),
      });
      const map = new Map<number, number>();
      (c.questions || []).forEach((q, idx) => {
        if (q.id) map.set(idx, q.id);
      });
      setSavedQuestionIds(map);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadCountries(); }, [loadCountries]);
  useEffect(() => { loadCampaign(); }, [loadCampaign]);

  const setField = <K extends keyof FeedbackCampaignInput>(k: K, v: FeedbackCampaignInput[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const setQuestion = (idx: number, patch: Partial<FeedbackQuestion>) =>
    setForm((p) => {
      const next = [...p.questions];
      next[idx] = { ...next[idx], ...patch };
      return { ...p, questions: next };
    });

  const addQuestion = () =>
    setForm((p) => ({ ...p, questions: [...p.questions, emptyQuestion(p.questions.length)] }));

  const removeQuestion = (idx: number) =>
    setForm((p) => {
      const next = p.questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order_index: i }));
      return { ...p, questions: next };
    });

  const moveQuestion = (idx: number, dir: -1 | 1) =>
    setForm((p) => {
      const target = idx + dir;
      if (target < 0 || target >= p.questions.length) return p;
      const next = [...p.questions];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...p, questions: next.map((q, i) => ({ ...q, order_index: i })) };
    });

  const setOptionLine = (qIdx: number, raw: string) => {
    const options = raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => ({ value: line, label: line }));
    setQuestion(qIdx, { options: options.length ? options : null });
  };

  const optionsAsLines = (q: FeedbackQuestion) =>
    (q.options || []).map((o) => o.label).join('\n');

  const validate = (): string | null => {
    if (!form.title.trim()) return 'Title is required';
    if (!form.starts_at || !form.ends_at) return 'Start and end dates are required';
    if (new Date(form.ends_at) <= new Date(form.starts_at)) return 'End must be after start';
    if (form.target_sample_size <= 0) return 'Target sample size must be > 0';
    for (const [i, q] of form.questions.entries()) {
      if (!q.text.trim()) return `Question ${i + 1} text is empty`;
      if (CHOICE_TYPES.has(q.type) && (!q.options || q.options.length < 2)) {
        return `Question ${i + 1} needs at least two options`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    try {
      const payload: FeedbackCampaignInput = {
        ...form,
        starts_at: fromLocalInput(form.starts_at),
        ends_at: fromLocalInput(form.ends_at),
        questions: form.questions.map((q, i) => ({ ...q, order_index: i })),
      };
      if (isEdit && id) {
        await updateFeedbackCampaign(Number(id), {
          title: payload.title,
          description: payload.description,
          country_id: payload.country_id,
          platform: payload.platform,
          target_sample_size: payload.target_sample_size,
          starts_at: payload.starts_at,
          ends_at: payload.ends_at,
          status: payload.status,
          min_app_version: payload.min_app_version,
          min_account_age_days: payload.min_account_age_days,
        });
        await syncQuestions(Number(id), payload.questions);
        navigate('/dashboard/feedback-campaigns');
      } else {
        const created = await createFeedbackCampaign(payload);
        navigate(`/dashboard/feedback-campaigns/edit/${created.id}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const syncQuestions = async (campaignId: number, questions: FeedbackQuestion[]) => {
    const existingIds = new Set(savedQuestionIds.values());
    const survivingIds = new Set<number>();

    for (let i = 0; i < questions.length; i++) {
      const q = { ...questions[i], order_index: i };
      const savedId = savedQuestionIds.get(i);
      if (savedId) {
        await updateFeedbackQuestion(savedId, q);
        survivingIds.add(savedId);
      } else if (q.id) {
        await updateFeedbackQuestion(q.id, q);
        survivingIds.add(q.id);
      } else {
        await addFeedbackQuestion(campaignId, q);
      }
    }
    for (const oldId of existingIds) {
      if (!survivingIds.has(oldId)) {
        await deleteFeedbackQuestion(oldId);
      }
    }
  };

  if (loading) {
    return <div className="feedback-editor-page"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="feedback-editor-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/dashboard/feedback-campaigns')}>
          <ArrowLeft size={16} /> Back
        </button>
        <h1>{isEdit ? 'Edit Campaign' : 'New Campaign'}</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="editor-card">
        <h2>Campaign details</h2>
        <div className="form-grid">
          <label className="form-field full">
            <span>Title</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="e.g. Post-purchase satisfaction"
            />
          </label>
          <label className="form-field full">
            <span>Description (optional)</span>
            <textarea
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => setField('description', e.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Country</span>
            <select
              value={form.country_id ?? ''}
              onChange={(e) => setField('country_id', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">All countries</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span>Platform</span>
            <select
              value={form.platform}
              onChange={(e) => setField('platform', e.target.value as FeedbackCampaignInput['platform'])}
            >
              <option value="both">Android &amp; iOS</option>
              <option value="android">Android only</option>
              <option value="ios">iOS only</option>
            </select>
          </label>
          <label className="form-field">
            <span>Target sample size</span>
            <input
              type="number"
              min={1}
              value={form.target_sample_size}
              onChange={(e) => setField('target_sample_size', Number(e.target.value))}
            />
          </label>
          <label className="form-field">
            <span>Status</span>
            <select
              value={form.status}
              onChange={(e) => setField('status', e.target.value as FeedbackCampaign['status'])}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label className="form-field">
            <span>Starts at</span>
            <input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setField('starts_at', e.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Ends at</span>
            <input
              type="datetime-local"
              value={form.ends_at}
              onChange={(e) => setField('ends_at', e.target.value)}
            />
          </label>
          <label className="form-field">
            <span>Min app version (optional)</span>
            <input
              type="number"
              min={0}
              value={form.min_app_version ?? ''}
              onChange={(e) =>
                setField('min_app_version', e.target.value ? Number(e.target.value) : null)
              }
            />
          </label>
          <label className="form-field">
            <span>Min account age (days)</span>
            <input
              type="number"
              min={0}
              value={form.min_account_age_days}
              onChange={(e) => setField('min_account_age_days', Number(e.target.value))}
            />
          </label>
        </div>
      </div>

      <div className="editor-card">
        <div className="card-header">
          <h2>Questions</h2>
          <button className="btn-secondary" onClick={addQuestion}>
            <Plus size={14} /> Add question
          </button>
        </div>

        {form.questions.length === 0 && (
          <div className="empty-state">No questions yet. Add at least one.</div>
        )}

        {form.questions.map((q, idx) => (
          <div className="question-row" key={idx}>
            <div className="question-header">
              <span className="question-index">Q{idx + 1}</span>
              <select
                value={q.type}
                onChange={(e) =>
                  setQuestion(idx, {
                    type: e.target.value as FeedbackQuestionType,
                    options: CHOICE_TYPES.has(e.target.value) ? q.options || [] : null,
                  })
                }
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <label className="inline-checkbox">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={(e) => setQuestion(idx, { required: e.target.checked })}
                /> Required
              </label>
              <div className="question-actions">
                <button className="icon-btn" onClick={() => moveQuestion(idx, -1)} title="Move up">
                  <ArrowUp size={14} />
                </button>
                <button className="icon-btn" onClick={() => moveQuestion(idx, 1)} title="Move down">
                  <ArrowDown size={14} />
                </button>
                <button className="icon-btn danger" onClick={() => removeQuestion(idx)} title="Remove">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <textarea
              className="question-text"
              rows={2}
              placeholder="Question text"
              value={q.text}
              onChange={(e) => setQuestion(idx, { text: e.target.value })}
            />
            {CHOICE_TYPES.has(q.type) && (
              <textarea
                className="question-options"
                rows={3}
                placeholder="One option per line"
                value={optionsAsLines(q)}
                onChange={(e) => setOptionLine(idx, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      <div className="editor-footer">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save campaign'}
        </button>
      </div>
    </div>
  );
};

export default FeedbackCampaignEditor;
